/**
 * The walkthrough card (design/2026-10-20-walkthroughs.md): a small fixed
 * card at the bottom right that says what each step shows, while the page
 * underneath stays usable. Drawn only; help/WalkthroughProvider owns the run.
 *
 * Above a MUI Dialog's backdrop (1300), so Next stays clickable when a step
 * opens one, and below the snackbar (1400). `data-help-pane` keeps the
 * runner's control search out of it (a step saying "click Next" must not find
 * the card's own button); `data-voice="off"` keeps the screen scan out too.
 * `data-walkthrough-step` and `data-status` are what the e2e spec waits on.
 */
import React from 'react'
import { Box, Button, IconButton, LinearProgress, Paper, Portal, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import type { Walkthrough } from '../../help/walkthroughs'

/** navigating: not ready yet (the page is opening or the step's action is running). */
export type WalkthroughStepStatus = 'navigating' | 'ready' | 'error'

export type WalkthroughCardView =
  | { kind: 'intro'; walkthrough: Walkthrough }
  | {
      kind: 'step'
      walkthrough: Walkthrough
      number: number
      total: number
      say: string
      did?: string
      problem?: string
      status: WalkthroughStepStatus
    }
  | { kind: 'done'; walkthrough: Walkthrough }

interface WalkthroughCardProps {
  view: WalkthroughCardView
  onStart: () => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
}

export const WALKTHROUGH_CARD_Z = 1350

export default function WalkthroughCard({ view, onStart, onNext, onBack, onClose }: WalkthroughCardProps) {
  const w = view.walkthrough
  const step = view.kind === 'step' ? view : undefined
  return (
    <Portal>
      <Paper
        role="region"
        aria-label="Walkthrough"
        elevation={8}
        data-help-pane=""
        data-voice="off"
        data-walkthrough={w.slug}
        data-walkthrough-card={view.kind}
        {...(step ? { 'data-walkthrough-step': step.number, 'data-status': step.status } : {})}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          zIndex: WALKTHROUGH_CARD_Z,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {step?.status === 'navigating' ? <LinearProgress color="info" sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}
        <Box sx={{ p: 2, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {step ? `Step ${step.number} of ${step.total}` : 'Walkthrough'}
              </Typography>
              <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>{w.title}</Typography>
            </Box>
            <IconButton size="small" aria-label="End walkthrough" onClick={onClose} sx={{ mt: -0.5, mr: -1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {view.kind === 'intro' && (
            <>
              <Typography variant="body2">{w.summary}</Typography>
              <Typography variant="caption" color="text.secondary">
                {w.steps.length} steps{w.issue ? ` · ${w.issue}` : ''}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" color="info" variant="contained" onClick={onStart}>Start</Button>
              </Box>
            </>
          )}

          {step && (
            <>
              <Typography variant="body2" aria-live="polite">{step.say}</Typography>
              {step.status === 'navigating' && !step.did && (
                <Typography variant="caption" color="text.secondary">Getting the page ready…</Typography>
              )}
              {step.did && (
                <Typography variant="body2" color="text.secondary">
                  <Box component="span" sx={{ fontWeight: 600 }}>Did:</Box> {step.did}
                </Typography>
              )}
              {step.problem && (
                <Typography variant="body2" color="error" role="alert">{step.problem}</Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" color="info" onClick={onBack} disabled={step.number === 1 || step.status === 'navigating'}>Back</Button>
                <Button size="small" color="info" variant="contained" onClick={onNext} disabled={step.status === 'navigating'}>
                  {step.number === step.total ? 'Finish' : 'Next'}
                </Button>
              </Box>
            </>
          )}

          {view.kind === 'done' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Done</Typography>
              </Box>
              <Typography variant="body2">You have been through every step. Nothing was saved for you.</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" color="info" onClick={onClose}>Close</Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Portal>
  )
}
